import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { createMap, haversine, fmtDuration } from "./mapUtils";
import { PersonSimpleRun, Bicycle, PersonSimpleWalk, Play, Stop } from "@phosphor-icons/react";

const SPORTS = [
  { id: "run", label: "Бег", icon: PersonSimpleRun },
  { id: "bike", label: "Велосипед", icon: Bicycle },
  { id: "walk", label: "Ходьба", icon: PersonSimpleWalk },
];

export default function WorkoutTracker({ onSaved }) {
  const [sport, setSport] = useState("run");
  const [recording, setRecording] = useState(false);
  const [stats, setStats] = useState({ dist: 0, speed: 0, maxSpeed: 0, elapsed: 0 });
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const lineRef = useRef(null);
  const state = useRef(null);
  const wakeLock = useRef(null);

  useEffect(() => {
    mapRef.current = createMap(mapEl.current, [55.751, 37.618], 15);
    return () => { mapRef.current?.remove(); stopWatch(); };
  }, []); // eslint-disable-line

  const stopWatch = () => {
    const s = state.current;
    if (s?.watchId != null) navigator.geolocation.clearWatch(s.watchId);
    if (s?.timer) clearInterval(s.timer);
    wakeLock.current?.release?.().catch(() => {});
    wakeLock.current = null;
  };

  const start = async () => {
    if (!navigator.geolocation) { toast.error("Геолокация не поддерживается этим браузером"); return; }
    const s = { points: [], dist: 0, maxSpeed: 0, startedAt: new Date().toISOString(), startMs: Date.now(), watchId: null, timer: null };
    state.current = s;
    if (lineRef.current) lineRef.current.remove();
    lineRef.current = L.polyline([], { color: "#0084ff", weight: 4 }).addTo(mapRef.current);
    try { wakeLock.current = await navigator.wakeLock?.request?.("screen"); } catch {}
    s.timer = setInterval(() => setStats((st) => ({ ...st, elapsed: Math.round((Date.now() - s.startMs) / 1000) })), 1000);
    s.watchId = navigator.geolocation.watchPosition((pos) => {
      const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, t: Date.now() };
      if (pos.coords.accuracy != null && pos.coords.accuracy > 60) return;
      const prev = s.points[s.points.length - 1];
      let speed = pos.coords.speed != null ? pos.coords.speed * 3.6 : 0;
      if (prev) {
        const d = haversine(prev, p);
        const dt = (p.t - prev.t) / 1000;
        if (d < 1 || dt < 1) return;
        if (d > 300) return;
        s.dist += d;
        if (pos.coords.speed == null) speed = (d / dt) * 3.6;
      }
      if (speed > s.maxSpeed && speed < 130) s.maxSpeed = speed;
      s.points.push(p);
      lineRef.current.addLatLng([p.lat, p.lng]);
      mapRef.current.setView([p.lat, p.lng], Math.max(mapRef.current.getZoom(), 15));
      setStats({ dist: s.dist, speed, maxSpeed: s.maxSpeed, elapsed: Math.round((Date.now() - s.startMs) / 1000) });
    }, (err) => {
      toast.error(err.code === 1 ? "Разрешите доступ к геолокации в браузере" : "GPS недоступен");
    }, { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
    setRecording(true);
    setStats({ dist: 0, speed: 0, maxSpeed: 0, elapsed: 0 });
  };

  const stop = async () => {
    const s = state.current;
    stopWatch();
    setRecording(false);
    if (!s || s.points.length < 2 || s.dist < 20) {
      toast.info("Маршрут слишком короткий — не сохраняем");
      return;
    }
    const duration = Math.round((Date.now() - s.startMs) / 1000);
    const pts = s.points.length > 4000 ? s.points.filter((_, i) => i % Math.ceil(s.points.length / 4000) === 0) : s.points;
    try {
      await api.post("/tracks", {
        sport, points: pts.map((p) => ({ lat: p.lat, lng: p.lng, t: p.t })),
        distance_m: s.dist, duration_s: duration,
        avg_speed_kmh: duration > 0 ? (s.dist / duration) * 3.6 : 0,
        max_speed_kmh: s.maxSpeed, started_at: s.startedAt,
      });
      toast.success("Маршрут сохранён!");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pace = stats.dist > 60 && stats.elapsed > 0 ? (stats.elapsed / 60) / (stats.dist / 1000) : null;

  return (
    <div className="space-y-3" data-testid="workout-tracker">
      {!recording && (
        <div className="flex gap-2">
          {SPORTS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setSport(id)} data-testid={`sport-${id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-colors ${sport === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2" data-testid="tracker-stats">
        {[["Скорость", `${stats.speed.toFixed(1)}`, "км/ч", "tracker-speed"],
          ["Дистанция", `${(stats.dist / 1000).toFixed(2)}`, "км", "tracker-distance"],
          ["Время", fmtDuration(stats.elapsed), "", "tracker-time"],
          ["Темп", pace ? `${Math.floor(pace)}:${String(Math.round((pace % 1) * 60)).padStart(2, "0")}` : "—", "мин/км", "tracker-pace"],
        ].map(([l, v, u, tid]) => (
          <div key={l} className="glass-card p-3 text-center" data-testid={tid}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
            <div className="font-heading text-xl font-extrabold num">{v}</div>
            {u && <div className="text-[10px] text-muted-foreground">{u}</div>}
          </div>
        ))}
      </div>
      <div ref={mapEl} className="h-[35vh] sm:h-[42vh] rounded-xl overflow-hidden border border-white/[0.08]" data-testid="tracker-map" />
      {!recording ? (
        <Button onClick={start} className="w-full h-12 text-base" data-testid="tracker-start-btn">
          <Play size={18} weight="fill" className="mr-2" /> Начать тренировку
        </Button>
      ) : (
        <Button onClick={stop} variant="destructive" className="w-full h-12 text-base" data-testid="tracker-stop-btn">
          <Stop size={18} weight="fill" className="mr-2" /> Завершить и сохранить
        </Button>
      )}
      {recording && <p className="text-[11px] text-muted-foreground text-center">Экран не погаснет во время записи. Держите приложение открытым.</p>}
    </div>
  );
}
